import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import apiServices from "../../../../../services/apiService";
import storageService from "../../../../../utils/storageService";

const POST_VISITOR_URL = "/Residential/ResVisitor/postVisitor";
const FREE_SLOTS_URL = "/Residential/ResVisitor/freePayParkSlots";
const LOOKUP_URL = "/Residential/ResVisitor/getAllVisitorByMobileNumber";
const USER_URL = "/Residential/ResUserDetails/getUserDetails";
const VEHICLE_TYPES = ["Car", "Bike", "Other"];

const emptyForm = (unitIds) => ({
    visitorName: "",
    mobileNumber: "",
    emailId: "",
    address: "",
    purposeOfVisit: "",
    additionalCount: "",
    entryDate: "",
    exitDate: "",
    unitIds: Array.isArray(unitIds) ? unitIds : [],
    vehicleNumber: "",
    vehicleType: "Car",
    parkingSlotId: null,
    image: null
});

const formatWhen = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const bookingStatus = (booking) => String(booking?.Status || booking?.status || "");

const statusStyle = {
    Held: "bg-amber-100 text-amber-700",
    Booked: "bg-blue-100 text-blue-700",
    Completed: "bg-green-100 text-green-700",
    Cancelled: "bg-gray-100 text-gray-500"
};

const bookingSource = (booking) => {
    const typeId = Number(booking.visitorTypeId || booking.VisitorTypeId);
    if (typeId === 2) return "Walk-in";
    if (typeId === 1) return "Invited";
    return "—";
};

const bookingLabel = (status) => (status === "Held" ? "Held (pending approval)" : status);

const fieldClass = "w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary";

const PayAndPark = () => {
    const { user } = useSelector((state) => state.auth);
    const roleName = String(user?.roleName || user?.RoleName || storageService.getItem("roleName") || "");
    const roleId = Number(user?.roleId || user?.RoleId || storageService.getItem("roleId"));
    const isUnitAdmin = roleId === 4 || roleId === 6 || roleName.toLowerCase().includes("unit");
    const unitLocked = isUnitAdmin;

    const [siteId, setSiteId] = useState(user?.siteId || user?.SiteId || storageService.getItem("siteId") || "");
    const [lockUnit, setLockUnit] = useState(unitLocked);
    const [units, setUnits] = useState([]);
    const [form, setForm] = useState(() => emptyForm(""));
    const [slots, setSlots] = useState([]);
    const [slotsNote, setSlotsNote] = useState("");
    const [bookings, setBookings] = useState([]);
    const [bookingFilter, setBookingFilter] = useState("active");
    const [saving, setSaving] = useState(false);
    const [photoName, setPhotoName] = useState("");

    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const loadUnits = async (forSiteId, lockedUnit) => {
        if (lockedUnit?.value) {
            setUnits([lockedUnit]);
            setField("unitIds", [lockedUnit.value]);
            return;
        }

        try {
            let allUnits = [];
            let pageNumber = 1;
            const pageSize = 1000;
            let hasMore = true;
            const maxPages = 20; // safety ceiling (up to 20,000 units)

            while (hasMore && pageNumber <= maxPages) {
                let url = `/UnitMaster/GetAllUnitMaster?pageNumber=${pageNumber}&pageSize=${pageSize}`;
                if (forSiteId) {
                    url += `&siteId=${forSiteId}`;
                }

                const unitRes = await apiServices({ method: "GET", url });
                const resData = unitRes?.data || {};
                const rows = resData.data || resData.Data || (Array.isArray(resData) ? resData : []);
                const totalCount = Number(resData.totalCount ?? resData.TotalCount ?? 0);

                if (Array.isArray(rows) && rows.length > 0) {
                    allUnits.push(...rows);
                    if (totalCount > 0 && allUnits.length >= totalCount) {
                        hasMore = false;
                    } else if (rows.length < pageSize) {
                        hasMore = false;
                    } else {
                        pageNumber++;
                    }
                } else {
                    hasMore = false;
                }
            }

            const formatted = allUnits
                .filter((unit) => !forSiteId || String(unit.SiteId ?? unit.siteId) === String(forSiteId))
                .map((unit) => {
                    const uName = unit.UnitName || unit.unitName || "";
                    const uNum = unit.UnitNumber || unit.unitNumber || unit.UnitNo || unit.unitNo || "";
                    const uId = unit.UnitId ?? unit.unitId ?? unit.Id ?? unit.id;
                    const label = [uName, uNum].filter(Boolean).join(" · ") || `Unit ${uId}`;
                    return {
                        label,
                        value: Number(uId)
                    };
                });

            // Natural sort so Unit 1, Unit 2, Unit 10 are in proper order
            formatted.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));

            setUnits(formatted);
        } catch (error) {
            console.error("Failed to load units:", error);
            toast.error("Could not load units");
        }
    };

    const loadBookings = async (forSiteId) => {
        const id = forSiteId || siteId;
        if (!id) return;
        const bookingRes = await apiServices({ method: "GET", url: `/VisitorParkingBooking?siteId=${id}` });
        const rows = bookingRes?.data?.Data || bookingRes?.data?.data || [];
        setBookings(Array.isArray(rows) ? rows : []);
    };

    const loadSlots = async (entryDate, exitDate) => {
        if (!siteId) return;
        const start = entryDate ? new Date(entryDate) : new Date();
        const params = { siteId, startDate: start.toISOString() };
        if (exitDate) params.endDate = new Date(exitDate).toISOString();
        const res = await apiServices({ method: "GET", url: FREE_SLOTS_URL, params });
        const body = res?.data || {};
        const rows = body.Data || body.data;
        if (body.IsSuccess === false || body.isSuccess === false || !Array.isArray(rows)) {
            setSlots([]);
            setSlotsNote(body.Message || body.message || "Could not load free slots.");
            return;
        }
        setSlots(rows);
        setSlotsNote(rows.length === 0 ? "No free Pay & Park slots for this time." : "");
        setForm((prev) => (
            prev.parkingSlotId && !rows.some((slot) => Number(slot.Id || slot.id) === Number(prev.parkingSlotId))
                ? { ...prev, parkingSlotId: null }
                : prev
        ));
    };

    useEffect(() => {
        const loadProfile = async () => {
            let resolvedSite = user?.siteId || user?.SiteId || storageService.getItem("siteId") || "";
            let lockedUnit = null;
            const uid = user?.id || user?.Id || user?.userId || user?.UserId;
            if (uid) {
                try {
                    const userRes = await apiServices({
                        method: "GET",
                        url: `${USER_URL}?Id=${uid}&PageNumber=1&PageSize=10`
                    });
                    const rows = userRes?.data?.Commuserdetailsdata || userRes?.data?.data || [];
                    const profile = Array.isArray(rows) ? rows[0] : rows;
                    if (profile) {
                        resolvedSite = profile.siteId || profile.SiteId || resolvedSite;
                        const profileRole = String(profile.RoleName || profile.roleName || roleName);
                        const profileUnit = profile.UnitId || profile.unitId;
                        if (profileRole.toLowerCase().includes("unit") && profileUnit) {
                            lockedUnit = {
                                label: profile.unitName || profile.UnitName || `Unit ${profileUnit}`,
                                value: Number(profileUnit)
                            };
                        }
                    }
                } catch (e) {
                    console.error("Error fetching user details in PayAndPark", e);
                }
            }
            if (!resolvedSite) {
                setSlotsNote("Sign in with a site account to load units and free slots.");
                return;
            }
            setSiteId(resolvedSite);
            const userBranchOrUnit = user?.branchorunitID || user?.BranchorunitID || user?.unitId || user?.UnitId || storageService.getItem("unitId") || storageService.getItem("branchorunitID");
            const unitToLock = lockedUnit || (unitLocked && userBranchOrUnit
                ? { label: user?.unitName || user?.UnitName || `Unit ${userBranchOrUnit}`, value: Number(userBranchOrUnit) }
                : null);
            setLockUnit(Boolean(unitToLock));
            await Promise.all([
                loadUnits(resolvedSite, unitToLock),
                loadBookings(resolvedSite)
            ]);
        };
        loadProfile().catch((err) => {
            console.error(err);
            toast.error("Could not load units");
        });
    }, [user?.id, user?.Id, user?.siteId, user?.SiteId]);

    useEffect(() => {
        if (!siteId) return;
        loadSlots(form.entryDate, form.exitDate).catch(() => setSlotsNote("Could not load free slots."));
    }, [siteId, form.entryDate, form.exitDate]);

    const lookupVisitor = async (mobile) => {
        const res = await apiServices({ method: "GET", url: `${LOOKUP_URL}/${mobile}` });
        const latest = res?.data?.data?.[0];
        if (!latest) return;
        const status = String(latest.status || latest.isActive || "").toLowerCase();
        if (status.includes("block") || status === "inactive") {
            toast.error("This visitor is blocked.");
            setForm((prev) => ({ ...prev, mobileNumber: "", visitorName: "", emailId: "", address: "" }));
            return;
        }
        setForm((prev) => ({
            ...prev,
            visitorName: prev.visitorName || latest.visitorName || "",
            emailId: prev.emailId || latest.emailId || "",
            address: prev.address || latest.location || latest.address || ""
        }));
    };

    const onMobile = (value) => {
        const mobile = value.replace(/\D/g, "").slice(0, 10);
        setField("mobileNumber", mobile);
        if (mobile.length === 10) lookupVisitor(mobile).catch(() => {});
    };

    const onPhoto = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setField("image", reader.result);
            setPhotoName(file.name);
        };
        reader.readAsDataURL(file);
    };

    const submit = async () => {
        if (!siteId) {
            toast.error("Your login has no site.");
            return;
        }
        if (!form.visitorName.trim() || form.mobileNumber.length !== 10 || form.unitIds.length === 0 || !form.purposeOfVisit.trim() || !form.address.trim()) {
            toast.warning("Enter name, a 10-digit mobile, at least one unit, coming from and reason for visit.");
            return;
        }
        if (!form.vehicleNumber.trim() || !form.vehicleType) {
            toast.warning("Enter the vehicle number and type.");
            return;
        }
        if (!form.parkingSlotId) {
            toast.warning("Select an available parking slot.");
            return;
        }
        if (form.entryDate && form.exitDate && new Date(form.exitDate) <= new Date(form.entryDate)) {
            toast.warning("Exit time must be after entry time.");
            return;
        }

        let image = form.image;
        if (image && image.includes(",")) image = image.split(",")[1];

        const payload = {
            siteId: Number(siteId),
            visitorName: form.visitorName.trim(),
            mobileNumber: form.mobileNumber,
            emailId: form.emailId.trim(),
            address: form.address.trim(),
            purposeOfVisit: form.purposeOfVisit.trim(),
            additionalCount: parseInt(form.additionalCount, 10) || 0,
            image,
            isActive: 1,
            unitIds: form.unitIds.map((id) => Number(id)),
            vehicleNumber: form.vehicleNumber.trim().toUpperCase(),
            vehicleType: form.vehicleType,
            parkingSlotId: Number(form.parkingSlotId)
        };
        if (form.entryDate) payload.expectedEntryTime = new Date(form.entryDate).toISOString();
        if (form.exitDate) payload.expectedExitTime = new Date(form.exitDate).toISOString();

        setSaving(true);
        try {
            const res = await apiServices({ method: "POST", url: POST_VISITOR_URL, data: payload });
            const body = res?.data || {};
            const ok = body.isSuccess === true || body.IsSuccess === true;
            if (!ok) {
                toast.error(body.message || body.Message || res?.message || "Could not create the visitor.");
                return;
            }
            const message = body.message || body.Message || "Visitor added.";
            if (String(message).toLowerCase().includes("not reserved")) toast.warning(message);
            else toast.success(message);
            setForm(emptyForm(lockUnit ? form.unitIds : []));
            setPhotoName("");
            await Promise.all([loadBookings(), loadSlots("", "")]);
        } finally {
            setSaving(false);
        }
    };

    const bookingFilters = [
        { id: "active", label: "Active", match: (status) => status === "Booked" || status === "Held" },
        { id: "held", label: "Held", match: (status) => status === "Held" },
        { id: "completed", label: "Completed", match: (status) => status === "Completed" },
        { id: "cancelled", label: "Cancelled", match: (status) => status === "Cancelled" },
        { id: "all", label: "All", match: () => true }
    ];
    const activeFilter = bookingFilters.find((item) => item.id === bookingFilter) || bookingFilters[0];
    const shownBookings = bookings
        .filter((booking) => activeFilter.match(bookingStatus(booking)))
        .sort((a, b) => new Date(b.StartDate || b.startDate || 0) - new Date(a.StartDate || a.startDate || 0));

    const selectedUnits = units.filter((unit) => form.unitIds.map(String).includes(String(unit.value)));

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading="Pay & Park"
                subHeading="Create the visitor for one or more units, add the vehicle, and choose a free Pay & Park slot. A unit that needs approval stays Pending on the same pass until they approve."
                primaryBtnText={saving ? "Creating..." : "Create visitor"}
                primaryBtnDisabled={saving}
                PrimaryOnClick={submit}
            />

            <form onSubmit={(event) => { event.preventDefault(); submit(); }} className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4 flex flex-col gap-6">
                <section>
                    <h3 className="text-sm font-bold mb-3">Visitor</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <label className="text-xs text-gray-500">Mobile
                            <input className={`${fieldClass} mt-1`} inputMode="numeric" maxLength={10} value={form.mobileNumber} onChange={(e) => onMobile(e.target.value)} placeholder="10-digit mobile" />
                        </label>
                        <label className="text-xs text-gray-500">Name
                            <input className={`${fieldClass} mt-1`} value={form.visitorName} onChange={(e) => setField("visitorName", e.target.value)} placeholder="Visitor name" />
                        </label>
                        <div>
                            <CustomSelect
                                label="Units"
                                isMulti={!lockUnit}
                                options={units}
                                value={lockUnit ? (selectedUnits[0] || null) : selectedUnits}
                                onChange={(selected) => {
                                    const next = lockUnit
                                        ? (selected ? [selected.value] : [])
                                        : (Array.isArray(selected) ? selected.map((item) => item.value) : []);
                                    setField("unitIds", next);
                                }}
                                placeholder="Select one or more units"
                                isDisabled={lockUnit}
                            />
                            <p className="text-[10px] text-gray-400 mt-1">Select every unit this visitor is going to. Each unit is approved on its own.</p>
                        </div>
                        <label className="text-xs text-gray-500">Coming from
                            <input className={`${fieldClass} mt-1`} value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="Location" />
                        </label>
                        <label className="text-xs text-gray-500">Reason for visit
                            <input className={`${fieldClass} mt-1`} value={form.purposeOfVisit} onChange={(e) => setField("purposeOfVisit", e.target.value)} placeholder="Purpose" />
                        </label>
                        <label className="text-xs text-gray-500">Email
                            <input className={`${fieldClass} mt-1`} type="email" value={form.emailId} onChange={(e) => setField("emailId", e.target.value)} placeholder="Optional" />
                        </label>
                        <label className="text-xs text-gray-500">Additional people
                            <input className={`${fieldClass} mt-1`} type="number" min="0" value={form.additionalCount} onChange={(e) => setField("additionalCount", e.target.value)} />
                        </label>
                        <label className="text-xs text-gray-500">Expected entry
                            <input className={`${fieldClass} mt-1`} type="datetime-local" value={form.entryDate} onChange={(e) => setField("entryDate", e.target.value)} />
                        </label>
                        <label className="text-xs text-gray-500">Expected exit
                            <input className={`${fieldClass} mt-1`} type="datetime-local" value={form.exitDate} onChange={(e) => setField("exitDate", e.target.value)} />
                        </label>
                        <label className="text-xs text-gray-500">Photo
                            <input className={`${fieldClass} mt-1`} type="file" accept="image/*" onChange={(e) => onPhoto(e.target.files?.[0])} />
                            {photoName && <span className="block mt-1 text-[10px] text-gray-400">{photoName}</span>}
                        </label>
                    </div>
                </section>

                <section>
                    <h3 className="text-sm font-bold mb-3">Vehicle</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="text-xs text-gray-500">Vehicle number
                            <input className={`${fieldClass} mt-1 uppercase`} value={form.vehicleNumber} onChange={(e) => setField("vehicleNumber", e.target.value.toUpperCase())} placeholder="MH12AB1234" />
                        </label>
                        <div>
                            <div className="text-xs text-gray-500 mb-1">Vehicle type</div>
                            <div className="flex gap-2">
                                {VEHICLE_TYPES.map((type) => (
                                    <button key={type} type="button" onClick={() => setField("vehicleType", type)} className={`flex-1 py-2 rounded-lg text-xs font-bold border ${form.vehicleType === type ? "border-blue-600 bg-blue-50 text-blue-700" : "border-gray-200 text-slate-600"}`}>
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-bold">Available slots</h3>
                        <span className="text-[11px] text-gray-500">{slots.length} free</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Slots already booked or held for this time are not shown. Changing the entry or exit time refreshes the list.</p>
                    {slotsNote && <p className="text-xs text-amber-700 mb-2">{slotsNote}</p>}
                    <div className="flex flex-wrap gap-2">
                        {slots.map((slot) => {
                            const id = Number(slot.Id || slot.id);
                            const selected = Number(form.parkingSlotId) === id;
                            return (
                                <button key={id} type="button" onClick={() => setField("parkingSlotId", selected ? null : id)} className={`min-w-[96px] px-3 py-2 rounded-xl border text-xs font-bold text-left ${selected ? "border-blue-500 bg-blue-100 ring-2 ring-blue-200" : "border-green-300 bg-white"}`}>
                                    {slot.SlotNumber || slot.slotNumber}
                                    <div className="font-normal text-[10px] text-gray-500">{selected ? "Selected" : "Free"}</div>
                                </button>
                            );
                        })}
                    </div>
                </section>
            </form>

            <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="text-sm font-bold">Bookings</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {bookingFilters.map((item) => {
                            const total = bookings.filter((booking) => item.match(bookingStatus(booking))).length;
                            return (
                                <button key={item.id} type="button" onClick={() => setBookingFilter(item.id)} className={`px-3 py-1 rounded-full text-[11px] font-bold border ${bookingFilter === item.id ? "bg-blue-600 text-white border-blue-600" : "bg-white border-gray-200 text-slate-600"}`}>
                                    {item.label} ({total})
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-gray-500">
                                <th className="py-2 pr-3">Visitor</th>
                                <th className="pr-3">Phone</th>
                                <th className="pr-3">Source</th>
                                <th className="pr-3">Slot</th>
                                <th className="pr-3">Vehicle</th>
                                <th className="pr-3">Start</th>
                                <th className="pr-3">End</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shownBookings.length === 0 && (
                                <tr><td colSpan={8} className="py-3 text-gray-400">No {activeFilter.id === "all" ? "" : `${activeFilter.label.toLowerCase()} `}bookings.</td></tr>
                            )}
                            {shownBookings.map((booking) => {
                                const status = bookingStatus(booking);
                                const vehicle = [booking.VehicleNumber, booking.VehicleType].filter(Boolean).join(" · ");
                                return (
                                    <tr key={booking.Id || booking.id} className="border-t">
                                        <td className="py-2 pr-3">{booking.VisitorName || "—"}</td>
                                        <td className="pr-3">{booking.VisitorPhone || "—"}</td>
                                        <td className="pr-3">{bookingSource(booking)}</td>
                                        <td className="pr-3">{booking.SlotNumber || booking.SlotId}</td>
                                        <td className="pr-3">{vehicle || "—"}</td>
                                        <td className="pr-3">{formatWhen(booking.StartDate || booking.startDate)}</td>
                                        <td className="pr-3">{formatWhen(booking.EndDate || booking.endDate)}</td>
                                        <td>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${statusStyle[status] || "bg-gray-100 text-gray-600"}`}>
                                                {bookingLabel(status) || "—"}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PayAndPark;
